// src/receptionist/screens/Behaviour.tsx
//
// How the AI behaves: voice, what it sends unprompted, what it refuses to
// handle, and what happens after hours. The booking mode is shown but locked —
// v1 captures and lets a human confirm (PRD §3.2), and pretending otherwise
// here would be the one setting that could promise a customer a slot we do not
// have.

import { useEffect, useState } from "react";
import { BellIcon, LockIcon, MessageCircleIcon, MoonIcon, ShieldAlertIcon } from "lucide-react";
import { PageHeader, Card, CardHeader, CardBody, Button } from "../components/kit";
import { Switch } from "@component/switch";
import { PickerInput, WaPreview } from "../components/ui";
import { cn } from "@oura/lib/cn";
import { TONE_LABELS, type MerchantConfig, type Tone } from "../merchant";
import type { MerchantScreenProps } from "../merchantState";

function Row({ title, hint, control }: { title: string; hint?: string; control: React.ReactNode }) {
    return (
        <div className="flex items-center gap-4 border-t border-gray-100 py-3 first:border-t-0">
            <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{title}</p>
                {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
            </div>
            {control}
        </div>
    );
}

/** The product's switch, so a toggle here is the same control as everywhere else. */
function Toggle({ on, onChange, label, disabled }: { on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
    return (
        <Switch checked={on} onCheckedChange={onChange} aria-label={label} disabled={disabled} className="flex-shrink-0" />
    );
}

export function BehaviourScreen({ merchant: server, onSave }: MerchantScreenProps) {
    // A local draft of the server's copy. Whenever the prop changes — after a
    // save returned the server's object, or after a failed save re-synced —
    // the draft is replaced wholesale, so the form never shows an optimistic
    // copy the server did not accept.
    const [m, setM] = useState<MerchantConfig>(server);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    useEffect(() => { setM(server); setDirty(false); }, [server]);
    const save = async () => {
        setSaving(true);
        try { if (await onSave(m)) setDirty(false); } finally { setSaving(false); }
    };
    const patch = (next: Partial<MerchantConfig>) => { setM((p) => ({ ...p, ...next })); setDirty(true); };

    /**
     * A sample of the chosen tone, NOT the message that goes out. The real
     * first message is `flow.opener`, edited on Conversation → Opener, and the
     * two are stored separately — this one is only here to make the tone
     * audible. It used to be captioned "the first thing a customer reads",
     * which was wrong: a merchant could change the tone, watch this line
     * change, and believe they had changed their greeting.
     */
    const toneSample = TONE_LABELS[m.tone].greeting.replace("{business}", m.name);

    return (
        <div>
            <PageHeader
                title="Behaviour"
                subtitle="How the receptionist sounds, what it sends on its own, and what it hands to a human."
                status={`Tone: ${TONE_LABELS[m.tone].label}`}
                action={<Button disabled={!dirty || saving} onClick={() => void save()}>{saving ? "Saving…" : "Save changes"}</Button>}
            />

            <div className="grid gap-5 md:grid-cols-2">
                <Card>
                    <CardHeader title="Tone" icon={MessageCircleIcon} description="Changes the wording, never the facts." />
                    <CardBody className="space-y-3 pt-0">
                        <div className="flex gap-2">
                            {(Object.keys(TONE_LABELS) as Tone[]).map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => patch({ tone: t })}
                                    className={cn(
                                        "flex-1 rounded-xl p-3 text-left ring-1 ring-inset transition-colors",
                                        m.tone === t ? "bg-blue-50 ring-blue-500" : "bg-white ring-gray-200 hover:bg-gray-50",
                                    )}
                                >
                                    <span className="block text-sm font-semibold text-gray-900">{TONE_LABELS[t].label}</span>
                                    <span className="mt-0.5 block text-xs text-gray-500">{TONE_LABELS[t].description}</span>
                                </button>
                            ))}
                        </div>
                        <WaPreview text={toneSample} note="How this tone sounds. Your actual opening message lives in Conversation → Opener." />
                    </CardBody>
                </Card>

                <Card>
                    <CardHeader title="Booking and reminders" icon={BellIcon} description="What goes out without anyone asking." />
                    <CardBody className="pt-0">
                        <Row
                            title="Capture, then your team confirms"
                            hint="The AI collects the request; a human confirms every slot. Automatic booking arrives with the calendar."
                            control={
                                <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-200">
                                    <LockIcon className="h-3 w-3" /> v1
                                </span>
                            }
                        />
                        {/* These two send outside WhatsApp's 24-hour window, so each
                            needs a Meta-approved template to deliver. The hint used to
                            assert the template WAS approved, which is not something this
                            screen reads: on a merchant whose pack is unsubmitted it
                            contradicted both the Conversation screen and the server,
                            which refuses to save a flow over exactly that. State the
                            requirement, and point at the surface that owns the status. */}
                        <Row
                            title="Reminder 24 hours before"
                            hint="Sends outside the 24-hour window, so it needs an approved WhatsApp template — check the pack on Business profile. The customer can reply to reschedule."
                            control={<Toggle on={m.remind24h} label="24 hour reminder" onChange={(v) => patch({ remind24h: v })} />}
                        />
                        {/* `remind2h` is a legacy field name: it gates the SAME-DAY
                            reminder, whose default offset is three hours before, not
                            two (ourteam-backend#4675). Labelled by what it does. */}
                        <Row
                            title="Same-day reminder"
                            hint="A few hours before the appointment, with directions. Also outside the window, so it needs an approved template."
                            control={<Toggle on={m.remind2h} label="Same-day reminder" onChange={(v) => patch({ remind2h: v })} />}
                        />
                        <Row
                            title="Nudge if they go quiet mid-booking"
                            hint="One free message inside 24 hours, then one template. Never more."
                            control={<Toggle on={m.dropOffNudge} label="Drop-off nudge" onChange={(v) => patch({ dropOffNudge: v })} />}
                        />
                    </CardBody>
                </Card>

                <Card>
                    <CardHeader title="Hand to a human" icon={ShieldAlertIcon} description="The AI stops and alerts your team." />
                    <CardBody className="pt-0">
                        {m.escalationRules.map((r) => (
                            <Row
                                key={r.key}
                                title={r.label}
                                hint={r.lockNote}
                                control={
                                    r.locked ? (
                                        <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-200">
                                            <LockIcon className="h-3 w-3" /> Always
                                        </span>
                                    ) : (
                                        <Toggle
                                            on={r.enabled}
                                            label={r.label}
                                            onChange={(v) => patch({
                                                escalationRules: m.escalationRules.map((x) => (x.key === r.key ? { ...x, enabled: v } : x)),
                                            })}
                                        />
                                    )
                                }
                            />
                        ))}
                    </CardBody>
                </Card>

                <Card>
                    <CardHeader title="After hours" icon={MoonIcon} description="What happens when someone messages a closed shop." />
                    <CardBody className="pt-0">
                        <Row
                            title="Answer and capture overnight"
                            hint="Answers questions, takes the request, and says when you open."
                            control={<Toggle on={m.afterHoursCapture} label="After-hours capture" onChange={(v) => patch({ afterHoursCapture: v })} />}
                        />
                        <Row
                            title="Morning digest"
                            hint="Overnight enquiries and pending confirmations, posted to the staff channel."
                            control={
                                <PickerInput
                                    kind="time"
                                    value={m.digestTime}
                                    aria-label="Morning digest time"
                                    onChange={(e) => patch({ digestTime: e.target.value })}
                                    className="w-32 flex-shrink-0"
                                />
                            }
                        />
                    </CardBody>
                </Card>
            </div>
        </div>
    );
}
